/* RESPONSIBLE TEAM: team-inbox-management */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  type InboxIdentifier,
  type InboxIdentifierWithCount,
  isSameInbox,
} from 'embercom/objects/inbox/inboxes/inbox';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import type InboxState from 'embercom/services/inbox-state';
import FaviconCounter from 'embercom/lib/favicon-counter';
import type ApplicationInstance from '@ember/application/instance';
import type Nexus from 'embercom/services/nexus';
import { type InboxContentsChangedEvent, NexusEventName } from 'embercom/services/nexus';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator, type TaskInstance, timeout } from 'ember-concurrency';
import type Session from 'embercom/services/session';
import ENV from 'embercom/config/environment';
import type TracingService from 'embercom/services/tracing';
import type InboxApi from 'embercom/services/inbox-api';
import Metrics from 'embercom/models/metrics';
import { captureException } from 'embercom/lib/sentry';
import { getOwner } from '@ember/application';

const ASSIGNEE_COUNTER_TYPES = ['all', 'unassigned', 'admin', 'team'];

export default class InboxCounters extends Service {
  @service declare inboxState: InboxState;
  @service declare nexus: Nexus;
  @service declare session: Session;
  @service declare tracing: TracingService;
  @service declare inboxApi: InboxApi;

  @tracked counts: { [key: string]: number } = {};
  private handler?: (e: InboxContentsChangedEvent) => void;
  private countersUpdatedHandler?: (e: any) => void;

  private visibleCounters: { [key: string]: { identifier: InboxIdentifier; count: number } } = {};
  private pendingInboxCounterUpdates: { [key: string]: InboxIdentifier } = {};
  private ttlTasks: { [key: string]: TaskInstance<void> } = {};
  private visibilityListener?: () => void;

  // Feature flag: team-inbox-realtime-counter-updates-fixes
  private missedCounterUpdates = 0;
  private readonly MISSED_UPDATES_THRESHOLD = 5;
  private readonly MISSED_UPDATES_TIMEOUT = 2500; // 2.5 seconds
  private missedUpdatesTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingInboxesForTimeout: InboxIdentifier[] = [];

  faviconCounter = new FaviconCounter(getOwner(this));

  private get isCounterFixEnabled(): boolean {
    return (
      this.session.workspace?.isFeatureEnabled('team-inbox-realtime-counter-updates-fixes') ?? false
    );
  }

  constructor(owner: ApplicationInstance) {
    super(owner);

    this.visibilityListener = () => {
      if (document.visibilityState === 'visible') {
        taskFor(this.fetchCountsForInboxesDebounced).perform();
      }
    };
    addEventListener('visibilitychange', this.visibilityListener);

    this.handler = this.handleInboxContentsChangedEvent.bind(this);
    this.countersUpdatedHandler = this.handleCountersUpdatedEvent.bind(this);
  }

  startListening() {
    if (this.handler) {
      this.nexus.addListener(NexusEventName.InboxContentsChanged, this.handler);
    }
    if (this.countersUpdatedHandler) {
      this.nexus.addListener(NexusEventName.CountersUpdated, this.countersUpdatedHandler);
    }
  }

  stopListening() {
    if (this.handler) {
      this.nexus.removeListener(NexusEventName.InboxContentsChanged, this.handler);
    }
    if (this.countersUpdatedHandler) {
      this.nexus.removeListener(NexusEventName.CountersUpdated, this.countersUpdatedHandler);
    }
  }

  willDestroy() {
    super.willDestroy();
    this.stopListening();
    if (this.visibilityListener) {
      removeEventListener('visibilitychange', this.visibilityListener);
    }
    if (this.missedUpdatesTimer) {
      clearTimeout(this.missedUpdatesTimer);
      this.missedUpdatesTimer = null;
    }
  }

  trackInbox(inbox: InboxIdentifier) {
    let identifier: InboxIdentifier = { type: inbox.type, id: inbox.id };
    let key = this.inboxKey(identifier);
    let data = this.visibleCounters[key];
    if (data) {
      data.count += 1;
    } else {
      this.nexus.subscribeTopics([this.inboxNexusTopic(inbox)]);
      data = { identifier, count: 1 };
      this.visibleCounters[key] = data;
    }
  }

  untrackInbox(inbox: InboxIdentifier) {
    let key = this.inboxKey(inbox);
    let data = this.visibleCounters[key];
    if (!data) {
      return;
    }
    data.count -= 1;
    if (data.count === 0) {
      this.nexus.unsubscribeTopics([this.inboxNexusTopic(inbox)]);
      delete this.visibleCounters[key];
    }
  }

  private inboxNexusTopic(inbox: InboxIdentifier) {
    return `inbox/${inbox.type}/${inbox.id}/InboxContentsChanged/counter`;
  }

  handleInboxContentsChangedEvent(e: InboxContentsChangedEvent) {
    let inboxes: InboxIdentifier[] = [];

    e.eventData.inboxes.forEach((inboxData) => {
      let inbox: InboxIdentifier = {
        type: inboxData.type,
        id: inboxData.id,
      };

      let key = this.inboxKey(inbox);
      if (!this.visibleCounters[key]) {
        return; // Not tracking this inbox
      }

      if (this.isCounterFixEnabled) {
        // NEW: Feature flag enabled - apply fixes

        // Issue #1: Skip counter fetch if this is the ACTIVE inbox being viewed
        // The list resource will handle the atomic update
        let isActiveInbox = isSameInbox(inbox, this.inboxState.activeInbox);
        let isMentionsType = inbox.type === InboxType.Mentions;
        if (isActiveInbox && !isMentionsType) {
          return; // Let the list resource handle this atomically (except mentions)
        }

        let hasCounterFlag = 'counter' in inboxData && inboxData.counter;
        let isAssigneeType = ASSIGNEE_COUNTER_TYPES.includes(inbox.type);

        // Issue #3: Skip fetch for assignee types WITHOUT counter flag
        // They're updated via CountersUpdated (pushed data) every 2-10s
        // Exception: If counter flag is explicitly set, respect it (edge case)
        if (isAssigneeType && !hasCounterFlag) {
          // Track missed update but don't fetch - wait for CountersUpdated push event
          this.missedCounterUpdates++;
          return;
        }

        // Fetch if counter flag is set (non-assignee types OR assignee types with explicit flag)
        if (hasCounterFlag) {
          inboxes.push(inbox);
        }
      } else {
        // OLD: Existing behavior - only check counter flag
        if (!('counter' in inboxData) || !inboxData.counter) {
          return;
        }
        inboxes.push(inbox);
      }
    });

    if (inboxes.length === 0) {
      return;
    }

    if (this.isCounterFixEnabled) {
      // Issue #2: Track missed updates for inboxes that will be fetched
      this.missedCounterUpdates++;

      // Add inboxes to pending list for timeout
      inboxes.forEach((inbox) => {
        let key = this.inboxKey(inbox);
        if (!this.pendingInboxesForTimeout.some((i) => this.inboxKey(i) === key)) {
          this.pendingInboxesForTimeout.push(inbox);
        }
      });

      // Start timeout if not already running
      if (!this.missedUpdatesTimer) {
        this.missedUpdatesTimer = setTimeout(() => {
          // Timeout reached - force refresh for pending inboxes
          if (this.pendingInboxesForTimeout.length > 0) {
            // Track metrics: timeout triggered (didn't reach threshold)
            Metrics.capture({
              increment: ['inbox.counter.force_refresh_by_timeout'],
              tags: {
                missed_updates_at_timeout: this.missedCounterUpdates,
                threshold: this.MISSED_UPDATES_THRESHOLD,
                timeout_ms: this.MISSED_UPDATES_TIMEOUT,
              },
            });

            this.refreshCounters(this.pendingInboxesForTimeout);
            this.resetMissedUpdatesTracking();
          }
        }, this.MISSED_UPDATES_TIMEOUT);
      }

      // Force refresh if threshold exceeded
      if (this.missedCounterUpdates > this.MISSED_UPDATES_THRESHOLD) {
        // Track metrics: threshold exceeded
        Metrics.capture({
          increment: ['inbox.counter.force_refresh_by_threshold'],
          tags: {
            missed_updates_at_threshold: this.missedCounterUpdates,
            threshold: this.MISSED_UPDATES_THRESHOLD,
          },
        });

        this.refreshCounters(inboxes);
        this.resetMissedUpdatesTracking();
        return;
      }
    }

    this.addPendingInboxCounterUpdates(inboxes);

    taskFor(this.fetchCountsForInboxesDebounced).perform();
  }

  private addPendingInboxCounterUpdates(inboxes: InboxIdentifier[]) {
    inboxes.forEach((inbox) => {
      this.pendingInboxCounterUpdates[`${inbox.type}-${inbox.id}`] = {
        type: inbox.type,
        id: inbox.id,
      };
    });
  }

  private handleCountersUpdatedEvent(_e: unknown) {
    if (this.isCounterFixEnabled) {
      // Track metrics: CountersUpdated received (cancelled force refresh)
      if (this.missedUpdatesTimer && this.missedCounterUpdates > 0) {
        Metrics.capture({
          increment: ['inbox.counter.counters_updated_received'],
          tags: {
            missed_updates_at_cancel: this.missedCounterUpdates,
            threshold: this.MISSED_UPDATES_THRESHOLD,
          },
        });
      }

      // Reset missed counter tracking on CountersUpdated event
      this.resetMissedUpdatesTracking();
    }
  }

  private resetMissedUpdatesTracking() {
    this.missedCounterUpdates = 0;
    this.pendingInboxesForTimeout = [];

    if (this.missedUpdatesTimer) {
      clearTimeout(this.missedUpdatesTimer);
      this.missedUpdatesTimer = null;
    }
  }

  @task({ keepLatest: true }) *fetchCountsForInboxesDebounced(): TaskGenerator<void> {
    // if we're not visible then there's no point fetching counters
    // we'll trigger this again when we next become visible
    if (document.visibilityState === 'hidden') {
      return;
    }

    yield timeout(this.jitter(ENV.APP._100MS, ENV.APP._500MS)); // wait for the second event (admin-specific) to hit, with jitter
    let updatesToRequest = Object.values(this.pendingInboxCounterUpdates);
    if (updatesToRequest.length === 0) {
      return;
    }

    try {
      this.pendingInboxCounterUpdates = {};
      let inboxes: InboxIdentifierWithCount[] =
        yield this.inboxApi.fetchInboxCounters(updatesToRequest);
      this.updateCountsFromInboxes(inboxes);
    } catch (e) {
      console.error('Error fetching counters', e);
      captureException(e);
      this.addPendingInboxCounterUpdates(updatesToRequest);
    } finally {
      yield timeout(this.jitter(ENV.APP._1000MS, ENV.APP._1500MS));
    }
  }

  jitter(min: number, max: number) {
    return min + Math.random() * (max - min);
  }

  refreshCounters(inboxes: InboxIdentifier[]) {
    this.addPendingInboxCounterUpdates(inboxes);
    taskFor(this.fetchCountsForInboxesDebounced).perform();
  }

  countForInbox(inbox: InboxIdentifier): number {
    return this.counts[this.inboxKey(inbox)] || 0;
  }

  decrementCount(inbox: InboxIdentifierWithCount) {
    let key = this.inboxKey(inbox);
    let counts = this.counts;
    let current = counts[key];
    if (current !== undefined && current > 0) {
      counts[this.inboxKey(inbox)] = current - 1;
      this.counts = counts;
    }

    this.updateCurrentInboxFavicon(inbox);
  }

  incrementCount(inbox: InboxIdentifierWithCount) {
    let key = this.inboxKey(inbox);
    let counts = this.counts;
    let current = counts[key];
    if (current !== undefined) {
      counts[this.inboxKey(inbox)] = current + 1;
      this.counts = counts;
    }

    this.updateCurrentInboxFavicon(inbox);
  }

  updateCount(inbox: InboxIdentifier, count: number, ttl?: number) {
    let counts = this.counts;
    counts[this.inboxKey(inbox)] = count;
    this.counts = counts;

    this.updateCurrentInboxFavicon(inbox);

    if (ttl) {
      this.trackCounterTTL(inbox, ttl);
    }
  }

  updateCountFromInbox(inbox: InboxIdentifierWithCount) {
    if (inbox.count !== undefined) {
      this.updateCount(inbox, inbox.count, inbox.valid_for);
    }
  }

  updateCountsFromInboxes(inboxes: InboxIdentifierWithCount[]) {
    inboxes.forEach((inbox) => {
      this.updateCountFromInbox(inbox);
    });
  }

  private trackCounterTTL(inbox: InboxIdentifier, ttl: number) {
    let key = this.inboxKey(inbox);
    this.ttlTasks[key]?.cancel();
    this.ttlTasks[key] = taskFor(this._trackCounterTTL).perform(inbox, ttl);
  }

  @task private *_trackCounterTTL(inbox: InboxIdentifier, ttl: number): TaskGenerator<void> {
    let key = this.inboxKey(inbox);
    yield timeout(ttl * ENV.APP._1000MS);
    if (this.visibleCounters[key]) {
      this.refreshCounters([inbox]);
    }
    delete this.ttlTasks[key];
  }

  private inboxKey(inbox: InboxIdentifier) {
    return `${inbox.type}-${inbox.id}`;
  }

  private updateCurrentInboxFavicon(inbox: InboxIdentifierWithCount) {
    if (isSameInbox(inbox, this.inboxState.activeInbox)) {
      let count = this.counts[this.inboxKey(inbox)];
      if (count !== undefined) {
        this.faviconCounter.updateFaviconCounter(count);
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    inboxCounters: InboxCounters;
    'inbox2-counters': InboxCounters;
  }
}
